/**
 * Projects list page.
 * Card grid layout with search and filters, matching Inception design.
 */

import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjects, useCreateProject } from "../hooks/useProjects";
import { useTags } from "../hooks/useTags";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceUrl } from "../lib/routes";
import { isProjectNameTaken } from "../lib/projectErrors";
import {
  FolderKanban,
  Plus,
  Search,
  FileText,
  Pin,
  X,
} from "lucide-react";

/** Rotate icon background colors across projects */
const ICON_BG_CLASSES = [
  "bg-nara-orange/15",
  "bg-nara-gold/15",
  "bg-nara-sage/25",
  "bg-muted",
];
const ICON_TEXT_CLASSES = [
  "text-nara-orange",
  "text-nara-gold",
  "text-nara-sage",
  "text-muted-foreground",
];

export function Projects() {
  const { t } = useTranslation();
  const { activeWorkspace } = useWorkspace();
  const { data: projectsData, isLoading } = useProjects();
  const createProject = useCreateProject();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urls = useWorkspaceUrl();
  const { tags, isError: tagsError } = useTags();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(
    searchParams.get("action") === "new"
  );
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [newProjectTagIds, setNewProjectTagIds] = useState<string[]>([]);

  const tagAccess = ((activeWorkspace?.settings?.tags) as any)?.access || "custom";
  const projects = projectsData?.projects || [];
  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTags = selectedTagIds.length === 0 ||
      (p.tags || []).some(t => selectedTagIds.includes(t.id));
    return matchesSearch && matchesTags;
  });

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      await createProject.mutateAsync({
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || undefined,
        tagIds: newProjectTagIds.length > 0 ? newProjectTagIds : undefined,
      });
      setShowCreateDialog(false);
      setNewProjectName("");
      setNewProjectDescription("");
      setNewProjectTagIds([]);
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const handleProjectClick = (projectId: string) => {
    if (urls) {
      navigate(urls.project(projectId));
    }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold">
            {t("projects.title")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {activeWorkspace
              ? t("projects.manageIn", { workspace: activeWorkspace.name })
              : t("projects.selectWorkspace")}
          </p>
        </div>
        {activeWorkspace && (
          <button
            onClick={() => setShowCreateDialog(true)}
            className="kii-btn kii-btn-primary shrink-0"
          >
            <Plus className="h-4 w-4" />
            {t("projects.newProject")}
          </button>
        )}
      </div>

      {!activeWorkspace ? (
        <div className="bento-card bento-surface p-8 text-center">
          <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            {t("projects.noWorkspace")}
          </h2>
          <p className="text-muted-foreground">
            {t("projects.noWorkspaceMessage")}
          </p>
        </div>
      ) : (
        <>
          {/* ── Search & Filters ── */}
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder={t("projects.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="kii-input pl-11"
                />
              </div>
              <button className="kii-btn kii-btn-primary shrink-0">
                <Search className="h-4 w-4" />
                Search
              </button>
              {(searchTerm || selectedTagIds.length > 0) && (
                <button
                  onClick={() => { setSearchTerm(""); setSelectedTagIds([]); }}
                  className="kii-btn kii-btn-outline shrink-0"
                >
                  <X className="h-4 w-4" />
                  Clear All
                </button>
              )}
            </div>
            {tagAccess !== "none" && tagsError && (
              <p className="text-sm text-red-600">{t("projects.tagsLoadError")}</p>
            )}
            {tagAccess !== "none" && tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => setSelectedTagIds(prev =>
                      prev.includes(tag.id)
                        ? prev.filter(id => id !== tag.id)
                        : [...prev, tag.id]
                    )}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selectedTagIds.includes(tag.id)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
                    }`}
                  >
                    {tag.color && (
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Count ── */}
          <p className="text-sm text-muted-foreground mb-5">
            {isLoading ? t("common.loading", "Loading...") : `${filteredProjects.length} items found`}
          </p>

          {/* ── Project Cards ── */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bento-card bento-surface min-h-[180px] animate-pulse"
                />
              ))}
            </div>
          ) : filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filteredProjects.map((project, idx) => {
                const colorIdx = idx % ICON_BG_CLASSES.length;
                const isActive = !project.deleted_at;

                return (
                  <div
                    key={project.id}
                    className="bento-card bento-surface bento-interactive flex flex-col justify-between"
                    onClick={() => handleProjectClick(project.id)}
                  >
                    {/* Top */}
                    <div>
                      <div className="flex items-start gap-4">
                        <div
                          className={`h-12 w-12 rounded-xl ${ICON_BG_CLASSES[colorIdx]} flex items-center justify-center shrink-0`}
                        >
                          <FolderKanban
                            className={`h-6 w-6 ${ICON_TEXT_CLASSES[colorIdx]}`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg truncate">
                              {project.name}
                            </h3>
                            {project.pinned && (
                              <Pin className="h-4 w-4 text-nara-orange shrink-0" />
                            )}
                          </div>
                          {project.tags && project.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {project.tags.map((tag) => (
                                <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                  {tag.color && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tag.color }} />}
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                          {project.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {project.description}
                            </p>
                          )}
                        </div>
                        <span
                          className={`kii-badge-inception shrink-0 ${
                            isActive
                              ? "bg-nara-sage text-nara-charcoal"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {isActive ? "Active" : "Draft"}
                        </span>
                      </div>
                    </div>

                    {/* Bottom */}
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-border/50">
                      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <FileText className="h-4 w-4" />
                        {project.document_count || 0} docs
                      </span>
                      {project.updated_at && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(project.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bento-card bento-surface p-8 text-center">
              <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-foreground mb-2">
                {searchTerm
                  ? t("projects.noMatching")
                  : t("projects.noProjects")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? t("projects.adjustSearch")
                  : t("projects.createFirst")}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="kii-btn kii-btn-primary"
                >
                  <Plus className="h-4 w-4" />
                  {t("projects.newProject")}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Create Project Dialog ── */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">
                {t("projects.createTitle")}
              </h2>
              <form onSubmit={handleCreateProject}>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      {t("projects.nameLabel")}
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={newProjectName}
                      onChange={(e) => {
                        setNewProjectName(e.target.value);
                        createProject.reset();
                      }}
                      placeholder={t("projects.namePlaceholder")}
                      className="kii-input"
                      autoFocus
                    />
                    {isProjectNameTaken(createProject.error) && (
                      <p role="alert" className="mt-1 text-sm text-destructive">
                        {t("projects.nameTaken")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      {t("projects.descriptionLabel")}
                    </label>
                    <textarea
                      id="description"
                      value={newProjectDescription}
                      onChange={(e) =>
                        setNewProjectDescription(e.target.value)
                      }
                      placeholder={t("projects.descriptionPlaceholder")}
                      rows={3}
                      className="w-full px-3 py-2 border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring resize-none bg-background text-foreground"
                    />
                  </div>
                  {tagAccess !== "none" && tags.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Tags
                      </label>
                      <div className="mt-1.5 space-y-2 max-h-40 overflow-y-auto">
                        {tags.map((tag) => (
                          <label key={tag.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newProjectTagIds.includes(tag.id)}
                              onChange={(e) => setNewProjectTagIds(prev =>
                                e.target.checked
                                  ? [...prev, tag.id]
                                  : prev.filter(id => id !== tag.id)
                              )}
                              className="rounded border-input"
                            />
                            <span className="flex items-center gap-1.5 text-sm">
                              {tag.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />}
                              {tag.name}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Assign tags to enable cross-project search
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateDialog(false);
                      createProject.reset();
                    }}
                    className="kii-btn kii-btn-ghost"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !newProjectName.trim() || createProject.isPending
                    }
                    className="kii-btn kii-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createProject.isPending
                      ? t("projects.creating")
                      : t("projects.createProject")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
